import React from 'react'

import { buildFilterControlProps } from 'features/catalog/hooks/catalog-filter-sidebar-handlers'
import {
  buildCatalogQueryActions,
  buildSavedQueryApplyHandler,
  readGhosts,
  withShownSearch,
} from 'features/catalog/hooks/catalog-yogiql-sidebar-handlers'
import { NO_GHOST_FILTERS } from 'features/catalog/store'
import { useCatalogQueryStore } from 'features/catalog/yogiql-store'
import type { FilterControlProps } from 'features/charts-v2'
import {
  type FeedbackQueryCodec,
  queryToPanel,
} from 'features/feedback/feedback-query'
// bully ignore[no-cross-feature-private-imports: same established seam use-catalog-filter-sidebar.ts already uses. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { useEffectiveFilterList } from 'features/filters/hooks/use-effective-filter-list'
import type { FilterUIState } from 'features/filters/store'
import { useFilterUIStore } from 'features/filters/store'
import { useProjectStore } from 'features/project/store'
import { useCurrentRoute } from 'hooks'
import type { ProjectState } from 'types/project'

/** `useCatalogFilterSidebar` under `enhanced_dialect`: the same props, read
 *  from and written to the panel's YogiQL query. `null` until the codec
 *  (catalog and filter options) is ready, so an edit never encodes against
 *  options that have not loaded. */
export function useCatalogYogiQLFilterSidebar(
  codec: FeedbackQueryCodec | null,
): FilterControlProps | null {
  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const { surface } = useCurrentRoute()
  const query = useCatalogQueryStore((state) => state.panels[0]!)
  const ghosts = useCatalogQueryStore((state) => state.ghosts[query.id])
  const shownSearch = useCatalogQueryStore(
    (state) => state.shownSearch[query.id],
  )
  const defaultFilterList = useFilterUIStore(
    (state: FilterUIState) => state.defaultFilterList,
  )
  const options = codec?.options
  const panel = React.useMemo(
    () =>
      options
        ? withShownSearch(
            queryToPanel(query, 0, options),
            query.query.filters,
            shownSearch,
          )
        : null,
    [query, options, shownSearch],
  )
  const ghostFilters = React.useMemo(
    () => (options ? readGhosts(ghosts, options) : NO_GHOST_FILTERS),
    [ghosts, options],
  )
  const actions = React.useMemo(
    () => (codec ? buildCatalogQueryActions(codec) : null),
    [codec],
  )

  // Single-field-scoped counts, as useCatalogFilterSidebar fetches them.
  const [fieldForCounts, setFieldForCounts] = React.useState<string | null>(
    null,
  )
  const countFields = React.useMemo(
    () => (fieldForCounts ? [fieldForCounts] : []),
    [fieldForCounts],
  )
  const filterOptions = useEffectiveFilterList({
    projectId,
    panel,
    defaultFilterList,
    fields: countFields,
  })

  if (!panel || !actions || !options) return null

  return {
    ...buildFilterControlProps({
      panel,
      ghostFilters,
      filterOptions,
      isFilterOptionsLoading: false,
      surface,
      setFieldForCounts,
      actions,
    }),
    onSavedQueryApply: buildSavedQueryApplyHandler(panel.id, options, surface),
  }
}
